/**
 * Quality Inspectors Marketplace
 * Certified inspection services with instant scheduling and digital reports
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Calendar
} from '@mui/material';
import {
  Assignment,
  Verified,
  Schedule,
  CheckCircle,
  Assessment,
  Engineering,
  Build,
  Security,
  Home,
  Business,
  LocationOn,
  Phone,
  Email,
  WhatsApp,
  Star,
  AccessTime,
  Person,
  Description,
  PhotoCamera,
  Download,
  Event,
  Warning,
  Info,
  Timeline,
  School,
  Award,
  TrendingUp
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/helpers';
import DialogCloseButton from '../../components/common/DialogCloseButton';

// Mock inspectors data
const mockInspectorsData = [
  {
    id: 1,
    name: 'Eng. Patricia Moyo',
    title: 'Senior Building Inspector',
    company: 'Zimbabwe Building Inspection Services',
    description: 'ZBCA certified inspector with 12+ years experience. Specialized in residential and commercial building inspections.',
    profileImage: '/api/placeholder/150/150',
    hourlyRate: 95,
    callOutFee: 150,
    experience: 12,
    completedInspections: 1847,
    rating: 4.9,
    reviewCount: 234,
    responseTime: '30 minutes',
    availability: 'Available today',
    location: 'Harare',
    serviceRadius: 75,
    certifications: [
      'ZBCA Certified Inspector',
      'Structural Engineering Degree',
      'Building Regulations Specialist',
      'SABS Standards Expert'
    ],
    languages: ['English', 'Shona'],
    specializations: [
      'Structural Inspections',
      'Foundation Assessment',
      'Roofing Systems',
      'Electrical Compliance',
      'Plumbing Systems',
      'Fire Safety'
    ],
    inspectionTypes: [
      {
        id: 'foundation',
        name: 'Foundation Inspection',
        description: 'Comprehensive foundation and footing assessment',
        duration: 3,
        price: 280,
        reportTime: '24 hours'
      },
      {
        id: 'structural',
        name: 'Structural Inspection',
        description: 'Complete structural integrity assessment',
        duration: 4,
        price: 380,
        reportTime: '48 hours'
      },
      {
        id: 'final',
        name: 'Final Building Inspection',
        description: 'Complete building compliance inspection',
        duration: 6,
        price: 650,
        reportTime: '72 hours'
      },
      {
        id: 'progress',
        name: 'Progress Inspection',
        description: 'Stage-by-stage construction progress review',
        duration: 2,
        price: 200,
        reportTime: '24 hours'
      }
    ],
    availability_schedule: {
      '2024-01-15': ['09:00', '14:00'],
      '2024-01-16': ['08:00', '11:00', '15:00'],
      '2024-01-17': ['10:00', '16:00'],
      '2024-01-18': [],
      '2024-01-19': ['09:00', '13:00', '16:00']
    },
    contact: {
      phone: '+263 4 123456',
      whatsapp: '+263 77 123456',
      email: 'patricia@zbis.co.zw'
    },
    isVerified: true,
    isPremium: true,
    isTopRated: true,
    sameDay: true,
    emergencyService: true
  },
  {
    id: 2,
    name: 'Arch. David Ncube',
    title: 'Building Code Specialist',
    company: 'Professional Inspection Solutions',
    description: 'Architecture background with expertise in building code compliance and municipal approvals.',
    profileImage: '/api/placeholder/150/150',
    hourlyRate: 85,
    callOutFee: 120,
    experience: 8,
    completedInspections: 1245,
    rating: 4.8,
    reviewCount: 187,
    responseTime: '45 minutes',
    availability: 'Available tomorrow',
    location: 'Bulawayo',
    serviceRadius: 100,
    certifications: [
      'Registered Architect',
      'ZBCA Inspector',
      'Municipal Code Expert',
      'Heritage Buildings Specialist'
    ],
    languages: ['English', 'Ndebele', 'Shona'],
    specializations: [
      'Building Code Compliance',
      'Municipal Approvals',
      'Heritage Inspections',
      'Accessibility Compliance',
      'Environmental Impact'
    ],
    inspectionTypes: [
      {
        id: 'compliance',
        name: 'Code Compliance Inspection',
        description: 'Building code and regulation compliance check',
        duration: 4,
        price: 340,
        reportTime: '48 hours'
      },
      {
        id: 'pre-approval',
        name: 'Pre-Approval Inspection',
        description: 'Inspection before municipal approval submission',
        duration: 3,
        price: 255,
        reportTime: '24 hours'
      },
      {
        id: 'heritage',
        name: 'Heritage Building Assessment',
        description: 'Specialized heritage building inspection',
        duration: 5,
        price: 425,
        reportTime: '5 days'
      }
    ],
    availability_schedule: {
      '2024-01-16': ['10:00', '14:00'],
      '2024-01-17': ['09:00', '13:00', '16:00'],
      '2024-01-18': ['11:00', '15:00'],
      '2024-01-19': ['08:00', '12:00']
    },
    contact: {
      phone: '+263 9 234567',
      whatsapp: '+263 77 234567',
      email: 'david@pis.co.zw'
    },
    isVerified: true,
    isPremium: false,
    isTopRated: true,
    sameDay: false,
    emergencyService: false
  },
  {
    id: 3,
    name: 'Eng. Sarah Chivero',
    title: 'Electrical & Fire Safety Inspector',
    company: 'Safety First Inspections',
    description: 'Electrical engineering specialist focusing on electrical systems and fire safety compliance.',
    profileImage: '/api/placeholder/150/150',
    hourlyRate: 110,
    callOutFee: 180,
    experience: 10,
    completedInspections: 987,
    rating: 4.7,
    reviewCount: 156,
    responseTime: '1 hour',
    availability: 'Available next week',
    location: 'Gweru',
    serviceRadius: 50,
    certifications: [
      'Electrical Engineer (EIZ)',
      'Fire Safety Inspector',
      'Electrical Installation Inspector',
      'Emergency Systems Specialist'
    ],
    languages: ['English', 'Shona'],
    specializations: [
      'Electrical Systems',
      'Fire Safety Systems',
      'Emergency Lighting',
      'Security Systems',
      'Power Distribution'
    ],
    inspectionTypes: [
      {
        id: 'electrical',
        name: 'Electrical System Inspection',
        description: 'Complete electrical installation assessment',
        duration: 3,
        price: 330,
        reportTime: '48 hours'
      },
      {
        id: 'fire-safety',
        name: 'Fire Safety Inspection',
        description: 'Fire safety systems and compliance check',
        duration: 4,
        price: 440,
        reportTime: '48 hours'
      },
      {
        id: 'emergency',
        name: 'Emergency Systems Inspection',
        description: 'Emergency lighting and alarm systems check',
        duration: 2,
        price: 220,
        reportTime: '24 hours'
      }
    ],
    availability_schedule: {
      '2024-01-22': ['09:00', '14:00'],
      '2024-01-23': ['10:00', '15:00'],
      '2024-01-24': ['08:00', '13:00']
    },
    contact: {
      phone: '+263 54 345678',
      whatsapp: '+263 77 345678',
      email: 'sarah@sfi.co.zw'
    },
    isVerified: true,
    isPremium: true,
    isTopRated: false,
    sameDay: true,
    emergencyService: true
  },
  {
    id: 4,
    name: 'QS Michael Dube',
    title: 'Quality Control Inspector',
    company: 'Construction Quality Assurance',
    description: 'Quantity surveyor with focus on construction quality control and material compliance.',
    profileImage: '/api/placeholder/150/150',
    hourlyRate: 75,
    callOutFee: 100,
    experience: 6,
    completedInspections: 643,
    rating: 4.6,
    reviewCount: 98,
    responseTime: '2 hours',
    availability: 'Available this week',
    location: 'Chitungwiza',
    serviceRadius: 40,
    certifications: [
      'Registered Quantity Surveyor',
      'Quality Control Specialist',
      'Materials Testing Certified',
      'SABS Standards Inspector'
    ],
    languages: ['English', 'Shona'],
    specializations: [
      'Quality Control',
      'Material Testing',
      'Construction Standards',
      'Cost Verification',
      'Progress Monitoring'
    ],
    inspectionTypes: [
      {
        id: 'quality',
        name: 'Quality Control Inspection',
        description: 'Construction quality and standards assessment',
        duration: 4,
        price: 300,
        reportTime: '48 hours'
      },
      {
        id: 'materials',
        name: 'Materials Compliance Check',
        description: 'Building materials quality and compliance',
        duration: 2,
        price: 150,
        reportTime: '24 hours'
      },
      {
        id: 'progress-audit',
        name: 'Progress Quality Audit',
        description: 'Ongoing construction quality monitoring',
        duration: 3,
        price: 225,
        reportTime: '24 hours'
      }
    ],
    availability_schedule: {
      '2024-01-17': ['11:00', '15:00'],
      '2024-01-18': ['09:00', '14:00'],
      '2024-01-19': ['10:00', '13:00', '16:00']
    },
    contact: {
      phone: '+263 4 456789',
      whatsapp: '+263 77 456789',
      email: 'michael@cqa.co.zw'
    },
    isVerified: true,
    isPremium: false,
    isTopRated: false,
    sameDay: false,
    emergencyService: false
  }
];

const InspectorsMarketplace = () => {
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState('');
  const [bookingStep, setBookingStep] = useState(0);
  const [bookingDetails, setBookingDetails] = useState({
    inspectionType: '',
    inspectionDate: null,
    inspectionTime: null,
    projectAddress: '',
    projectDescription: '',
    urgency: 'standard',
    additionalRequirements: '',
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      company: ''
    }
  });

  // Load inspectors data
  useEffect(() => {
    const loadInspectors = async () => {
      setLoading(true);
      setTimeout(() => {
        // TODO: Replace with real API call to /api/marketplace/inspectors
        setInspectors([]); // Empty - no mock data
        setLoading(false);
      }, 500);
    };

    loadInspectors();
  }, []);

  // Handle book inspection
  const handleBookInspection = (inspector) => {
    setSelectedInspector(inspector);
    setBookingStep(0);
    setBookingDialog(true);
  };

  // Calculate total cost
  const calculateTotalCost = () => {
    if (!selectedInspector || !bookingDetails.inspectionType) return 0;
    
    const inspection = selectedInspector.inspectionTypes.find(
      type => type.id === bookingDetails.inspectionType
    );
    
    let total = inspection?.price || 0;
    total += selectedInspector.callOutFee;
    
    if (bookingDetails.urgency === 'urgent') {
      total *= 1.5; // 50% surcharge for urgent
    } else if (bookingDetails.urgency === 'emergency') {
      total *= 2; // 100% surcharge for emergency
    }
    
    return total;
  };

  // Booking Dialog Component
  const BookingDialog = () => {
    const steps = ['Inspection Type', 'Schedule', 'Project Details', 'Confirmation'];

    const renderStepContent = () => {
      switch (bookingStep) {
        case 0:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Select Inspection Type
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose the type of inspection you need
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <RadioGroup
                  value={bookingDetails.inspectionType}
                  onChange={(e) => setBookingDetails({...bookingDetails, inspectionType: e.target.value})}
                >
                  {selectedInspector?.inspectionTypes.map((inspection) => (
                    <Card key={inspection.id} sx={{ mb: 2, border: bookingDetails.inspectionType === inspection.id ? 2 : 1, borderColor: bookingDetails.inspectionType === inspection.id ? 'primary.main' : 'divider' }}>
                      <CardContent>
                        <FormControlLabel
                          value={inspection.id}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {inspection.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {inspection.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Chip 
                                  icon={<AccessTime />} 
                                  label={`${inspection.duration} hours`} 
                                  size="small" 
                                  variant="outlined" 
                                />
                                <Chip 
                                  icon={<Description />} 
                                  label={`Report in ${inspection.reportTime}`} 
                                  size="small" 
                                  variant="outlined" 
                                />
                                <Chip 
                                  label={formatCurrency(inspection.price)} 
                                  size="small" 
                                  color="primary" 
                                />
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Urgency Level
                </Typography>
                <RadioGroup
                  value={bookingDetails.urgency}
                  onChange={(e) => setBookingDetails({...bookingDetails, urgency: e.target.value})}
                  row
                >
                  <FormControlLabel 
                    value="standard" 
                    control={<Radio />} 
                    label="Standard (No extra charge)" 
                  />
                  <FormControlLabel 
                    value="urgent" 
                    control={<Radio />} 
                    label="Urgent (+50%)" 
                  />
                  {selectedInspector?.emergencyService && (
                    <FormControlLabel 
                      value="emergency" 
                      control={<Radio />} 
                      label="Emergency (+100%)" 
                    />
                  )}
                </RadioGroup>
              </Grid>
            </Grid>
          );

        case 1:
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Schedule Inspection
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <DatePicker
                    label="Inspection Date"
                    value={bookingDetails.inspectionDate}
                    onChange={(date) => setBookingDetails({...bookingDetails, inspectionDate: date})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={new Date()}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TimePicker
                    label="Preferred Time"
                    value={bookingDetails.inspectionTime}
                    onChange={(time) => setBookingDetails({...bookingDetails, inspectionTime: time})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Inspector availability: {selectedInspector?.availability}
                    </Typography>
                    <Typography variant="caption">
                      Response time: {selectedInspector?.responseTime} • 
                      Service radius: {selectedInspector?.serviceRadius}km from {selectedInspector?.location}
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Time Slots
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      📅 Available slots will be shown based on selected date
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          );

        case 2:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Project Details
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Address"
                  value={bookingDetails.projectAddress}
                  onChange={(e) => setBookingDetails({...bookingDetails, projectAddress: e.target.value})}
                  placeholder="Full address where inspection will take place"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Project Description"
                  value={bookingDetails.projectDescription}
                  onChange={(e) => setBookingDetails({...bookingDetails, projectDescription: e.target.value})}
                  placeholder="Describe the construction project, specific areas to inspect, and any concerns..."
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Requirements"
                  value={bookingDetails.additionalRequirements}
                  onChange={(e) => setBookingDetails({...bookingDetails, additionalRequirements: e.target.value})}
                  placeholder="Any special requirements, site conditions, or additional documentation needed..."
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={bookingDetails.contactInfo.name}
                  onChange={(e) => setBookingDetails({
                    ...bookingDetails,
                    contactInfo: {...bookingDetails.contactInfo, name: e.target.value}
                  })}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Company (Optional)"
                  value={bookingDetails.contactInfo.company}
                  onChange={(e) => setBookingDetails({
                    ...bookingDetails,
                    contactInfo: {...bookingDetails.contactInfo, company: e.target.value}
                  })}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={bookingDetails.contactInfo.email}
                  onChange={(e) => setBookingDetails({
                    ...bookingDetails,
                    contactInfo: {...bookingDetails.contactInfo, email: e.target.value}
                  })}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={bookingDetails.contactInfo.phone}
                  onChange={(e) => setBookingDetails({
                    ...bookingDetails,
                    contactInfo: {...bookingDetails.contactInfo, phone: e.target.value}
                  })}
                  required
                />
              </Grid>
            </Grid>
          );

        case 3:
          const selectedInspectionType = selectedInspector?.inspectionTypes.find(
            type => type.id === bookingDetails.inspectionType
          );
          
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Booking Confirmation
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedInspector?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedInspector?.company}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Inspection Type</Typography>
                      <Typography variant="body1">
                        {selectedInspectionType?.name}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">
                        {bookingDetails.inspectionDate ? formatDateTime(bookingDetails.inspectionDate) : 'Not set'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Project Address</Typography>
                      <Typography variant="body1">
                        {bookingDetails.projectAddress || 'Not provided'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>Cost Breakdown</Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Inspection Fee</Typography>
                        <Typography variant="body2">
                          {formatCurrency(selectedInspectionType?.price || 0)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Call-out Fee</Typography>
                        <Typography variant="body2">
                          {formatCurrency(selectedInspector?.callOutFee || 0)}
                        </Typography>
                      </Box>

                      {bookingDetails.urgency !== 'standard' && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {bookingDetails.urgency === 'urgent' ? 'Urgent' : 'Emergency'} Surcharge
                          </Typography>
                          <Typography variant="body2">
                            {bookingDetails.urgency === 'urgent' ? '+50%' : '+100%'}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Total</Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(calculateTotalCost())}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Alert severity="info">
                Your inspection request will be sent to the inspector for confirmation. 
                You will receive a response within {selectedInspector?.responseTime}.
              </Alert>
            </Box>
          );

        default:
          return null;
      }
    };

    return (
      <Dialog
        open={bookingDialog}
        onClose={() => setBookingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setBookingDialog(false)} />
        <DialogTitle>
          Book Inspection - {selectedInspector?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={bookingStep} sx={{ mb: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialog(false)}>
            Cancel
          </Button>
          {bookingStep > 0 && (
            <Button onClick={() => setBookingStep(bookingStep - 1)}>
              Back
            </Button>
          )}
          {bookingStep < 3 ? (
            <Button
              variant="contained"
              onClick={() => setBookingStep(bookingStep + 1)}
              disabled={
                (bookingStep === 0 && !bookingDetails.inspectionType) ||
                (bookingStep === 1 && !bookingDetails.inspectionDate) ||
                (bookingStep === 2 && (!bookingDetails.projectAddress || !bookingDetails.contactInfo.name || !bookingDetails.contactInfo.email))
              }
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                alert('Inspection booking submitted successfully! You will receive confirmation within ' + selectedInspector?.responseTime + '.');
                setBookingDialog(false);
              }}
            >
              Confirm Booking
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // Inspector Card Component
  const InspectorCard = ({ inspector }) => (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header with photo and basic info */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Avatar
            src={inspector.profileImage}
            sx={{ width: 64, height: 64 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {inspector.name}
              </Typography>
              {inspector.isVerified && <Verified color="primary" sx={{ fontSize: 18 }} />}
              {inspector.isPremium && <Star color="warning" sx={{ fontSize: 18 }} />}
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {inspector.title}
            </Typography>
            
            <Typography variant="caption" color="text.secondary">
              {inspector.company}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            {inspector.isTopRated && (
              <Chip label="Top Rated" size="small" color="warning" sx={{ mb: 0.5 }} />
            )}
            {inspector.sameDay && (
              <Chip label="Same Day" size="small" color="success" />
            )}
          </Box>
        </Box>

        {/* Rating and stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Star sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {inspector.rating}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({inspector.reviewCount} reviews)
            </Typography>
          </Box>
        </Box>

        {/* Location and availability */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {inspector.location} • {inspector.serviceRadius}km radius
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {inspector.availability}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {inspector.description}
        </Typography>

        {/* Specializations */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {inspector.specializations.slice(0, 3).map((spec, index) => (
            <Chip key={index} label={spec} size="small" variant="outlined" />
          ))}
          {inspector.specializations.length > 3 && (
            <Chip label={`+${inspector.specializations.length - 3}`} size="small" variant="outlined" />
          )}
        </Box>

        {/* Key stats */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {inspector.completedInspections}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Inspections
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {inspector.experience}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Years Exp.
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {inspector.responseTime}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Response
            </Typography>
          </Grid>
        </Grid>

        {/* Pricing */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            From {formatCurrency(Math.min(...inspector.inspectionTypes.map(t => t.price)))}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            +{formatCurrency(inspector.callOutFee)} call-out fee
          </Typography>
        </Box>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Info />}
              size="small"
              onClick={() => {/* View profile */}}
            >
              View Profile
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Schedule />}
              onClick={() => handleBookInspection(inspector)}
              size="small"
            >
              Book Now
            </Button>
          </Grid>
        </Grid>

        {/* Contact options */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <IconButton size="small" color="success">
            <WhatsApp />
          </IconButton>
          <IconButton size="small" color="primary">
            <Phone />
          </IconButton>
          <IconButton size="small" color="info">
            <Email />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );

  return (
    <>
      <MarketplaceLayout
        category="inspectors"
        title="Quality Inspectors"
        description="Certified building inspectors with instant scheduling. Get professional inspection reports with photo documentation within 24-72 hours."
        data={inspectors}
        loading={loading}
        totalResults={inspectors.length}
        customActions={[
          {
            icon: <Event />,
            name: 'My Inspections',
            action: () => console.log('View inspections')
          },
          {
            icon: <Assessment />,
            name: 'Reports',
            action: () => console.log('View reports')
          }
        ]}
      >
        {/* Inspectors Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={6} lg={4}>
                <Skeleton variant="rectangular" height={500} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {inspectors.map((inspector) => (
              <Grid item key={inspector.id} xs={12} sm={6} md={6} lg={4}>
                <InspectorCard inspector={inspector} />
              </Grid>
            ))}
          </Grid>
        )}
      </MarketplaceLayout>

      {/* Booking Dialog */}
      <BookingDialog />
    </>
  );
};

export default InspectorsMarketplace;